import jsPDF from "jspdf";
import { POLYGONS, VARS } from "../ThreeJs/Global/variables";
import sflLogo from "../assets/ui_revamp/SFL 3.png";
import compassIcon from "../assets/ui_revamp/Group 1171275125.svg";
import { uploadDesignerReport } from "./reportUpload";
import { toast } from "../lib/toast";
import { buildMapTileURL } from "./CONSTANT";
import {
  fitImageContain,
  getPreferredOrganizationLogoUrl,
  getPreferredOrganizationSettings,
  loadImageAsDataUrl,
  loadImageDimensions,
  trimImageWhitespace,
} from "./reportBranding";

const colors = {
  bg: [246, 247, 249],
  text: [30, 42, 54],
  muted: [114, 124, 134],
  border: [214, 219, 224],
  card: [255, 255, 255],
  wire: [81, 232, 244],
  focus: [142, 149, 158],
  dimension: [156, 163, 171],
  dimensionLight: [192, 198, 205],
  deep: [6, 37, 57],
  accent: [30, 168, 236],
  highestMarker: [133, 232, 106],
  obstacle: [255, 56, 158],
  setback: [246, 232, 141],
};

const px = (value) => value * 0.75;

const DEFAULT_FLIGHT = {
  pilot: "Ensite",
  droneId: "163DG7G0014C3",
  scanDate: "03/04/2024",
  start: "3:28 PM EST",
  end: "3:35 PM EST",
  duration: "00:07:40",
};

const DEFAULT_ALTITUDES = [
  { label: "Full Subject Photo", value: "193 ft" },
  { label: "Safe Height", value: "160 ft" },
  { label: "Horizon Photos (While traveling)", value: "66 ft" },
];

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatAltitudeFeet = (value, fallback) => {
  const numeric = toFiniteNumber(value);
  return numeric == null ? fallback : `${numeric} ft`;
};

const buildDynamicFacetAltitudes = (currentDesign) => {
  const settings = getPreferredOrganizationSettings(currentDesign);
  const heightOfHouse = toFiniteNumber(currentDesign?.height_of_house ?? settings?.height_of_house);
  const mustHeight = toFiniteNumber(currentDesign?.must_height ?? settings?.must_height);

  return [
    {
      label: "Full Subject Photo",
      value: formatAltitudeFeet(heightOfHouse == null ? null : heightOfHouse + 45, "193 ft"),
    },
    {
      label: "Safe Height",
      value: formatAltitudeFeet(mustHeight, "160 ft"),
    },
    {
      label: "Horizon Photos (While traveling)",
      value: formatAltitudeFeet(heightOfHouse == null ? null : heightOfHouse + 15, "66 ft"),
    },
  ];
};

const FLIGHT_INFO_IMAGE_HEIGHT = 250;
const FLIGHT_INFO_MAP_STATIC_WIDTH = 640;

const buildFlightInfoMapImage = async (currentDesign, targetAspectRatio) => {
  const latitude = Number(currentDesign?.latitude);
  const longitude = Number(currentDesign?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || targetAspectRatio <= 0) {
    return "";
  }

  const requestedHeight = Math.max(
    64,
    Math.min(640, Math.round(FLIGHT_INFO_MAP_STATIC_WIDTH / targetAspectRatio))
  );
  const staticMapUrl = buildMapTileURL({
    centerLat: latitude,
    centerLng: longitude,
    width: FLIGHT_INFO_MAP_STATIC_WIDTH,
    height: requestedHeight,
    markers: [{ lat: latitude, lng: longitude, color: "red", size: "mid" }],
  });
  if (!staticMapUrl) return "";
  return loadImageAsDataUrl(staticMapUrl);
};

let compassIconDataUrl = "";
const COMPASS_BASE_W = 26;
const COMPASS_BASE_H = 40;
const COMPASS_ASPECT = COMPASS_BASE_W / COMPASS_BASE_H;

const loadCompassIconData = async () => {
  if (compassIconDataUrl) return compassIconDataUrl;
  if (typeof window === "undefined" || typeof document === "undefined") return "";
  try {
    const img = new Image();
    img.src = compassIcon;
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    // Keep this modest; very large rasterized compass images inflate PDF size.
    const targetH = 600;
    const targetW = Math.max(1, Math.round(targetH * COMPASS_ASPECT));
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.clearRect(0, 0, targetW, targetH);
    ctx.drawImage(img, 0, 0, targetW, targetH);
    compassIconDataUrl = canvas.toDataURL("image/png");
    return compassIconDataUrl;
  } catch (_e) {
    return "";
  }
};

const pickFirstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
  }
  return "";
};

const toFixedOrDash = (v, d = 1) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : "-";
};

const formatDecimal = (value, digits = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  if (Math.abs(n) < 1e-9) return "0";
  return n.toFixed(digits).replace(/\.?0+$/, "");
};

const formatDistanceFtIn = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  const sign = n < 0 ? "-" : "";
  const totalInches = Math.round(Math.abs(n) * 12);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return `${sign}${feet}'${inches}''`;
};

const getCustomerName = (design) =>
  pickFirstNonEmpty(design?.customerName, design?.customer_name, design?.name) || "N/A";

const getAddress = (design) => pickFirstNonEmpty(design?.address) || "N/A";

const getStoredDesignerName = () => {
  if (typeof window === "undefined") return "";

  try {
    return window.localStorage.getItem("designerName") || "";
  } catch (error) {
    return "";
  }
};

const getDesignerName = (design) =>
  pickFirstNonEmpty(
    design?.designerName,
    design?.designer_name,
    design?.createdByName,
    getStoredDesignerName()
  ) || "N/A";

const getSegmentLabel = (polygon, index) => {
  const fromElement =
    polygon?.label?.element?.innerText ||
    polygon?.label?.element?.textContent ||
    polygon?.label?.textContent;
  if (typeof fromElement === "string" && fromElement.trim()) return fromElement.trim();
  if (typeof polygon?.label === "string" && polygon.label.trim()) return polygon.label.trim();
  return String(index + 1);
};

const dedupePoints = (points = []) => {
  const seen = new Set();
  const out = [];
  points.forEach((p) => {
    const x = Number(p?.x);
    const y = Number(p?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    const key = `${x.toFixed(6)}:${y.toFixed(6)}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ x, y });
  });
  return out;
};

const sortPointsClockwise = (points = []) => {
  if (points.length <= 2) return points;
  const cx = points.reduce((s, p) => s + p.x, 0) / points.length;
  const cy = points.reduce((s, p) => s + p.y, 0) / points.length;
  return [...points].sort(
    (a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx)
  );
};

const getSegmentPoints2d = (polygon) => {
  const edgePoints = Array.isArray(polygon?.edges)
    ? polygon.edges.map((e) => ({ x: e?.point1?.x, y: e?.point1?.y }))
    : [];
  const cleanEdges = dedupePoints(edgePoints);
  if (cleanEdges.length >= 3) return cleanEdges;

  const pos = polygon?.shape?.geometry?.attributes?.position?.array;
  if (!pos || typeof pos.length !== "number") return [];
  const geoPoints = [];
  for (let i = 0; i < pos.length; i += 3) geoPoints.push({ x: pos[i], y: pos[i + 1] });
  return sortPointsClockwise(dedupePoints(geoPoints));
};

const getSegmentEdges3d = (polygon) => {
  if (!Array.isArray(polygon?.edges)) return [];
  return polygon.edges
    .map((edge) => {
      const p1 = edge?.point1;
      const p2 = edge?.point2;
      const a = {
        x: Number(p1?.x),
        y: Number(p1?.y),
        z: Number(p1?.z),
      };
      const b = {
        x: Number(p2?.x),
        y: Number(p2?.y),
        z: Number(p2?.z),
      };
      const validA = Number.isFinite(a.x) && Number.isFinite(a.y) && Number.isFinite(a.z);
      const validB = Number.isFinite(b.x) && Number.isFinite(b.y) && Number.isFinite(b.z);
      if (!validA || !validB) return null;
      return { a, b };
    })
    .filter(Boolean);
};

const getSegmentObstacles2d = (polygon) => {
  if (!Array.isArray(polygon?.obstructions)) return [];
  return polygon.obstructions
    .map((obs) => {
      const cords = Array.isArray(obs?.cords) ? obs.cords : [];
      const points = cords
        .map((p) => ({
          x: Number(p?.x),
          y: Number(p?.y),
        }))
        .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
      if (points.length < 3) return null;
      const deduped = dedupePoints(points);
      if (deduped.length < 3) return null;
      return deduped;
    })
    .filter(Boolean);
};

const getSegmentSetback2d = (polygon) => {
  const setBacks = polygon?.setBacks;
  if (!setBacks?.isSet || !Array.isArray(setBacks?.edgesData)) return null;
  const points = setBacks.edgesData
    .map((edge) => ({
      x: Number(edge?.point1?.x),
      y: Number(edge?.point1?.y),
    }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  if (points.length >= 2) {
    const first = points[0];
    const last = points[points.length - 1];
    if (Math.abs(first.x - last.x) < 1e-6 && Math.abs(first.y - last.y) < 1e-6) {
      points.pop();
    }
  }
  return points.length >= 3 ? points : null;
};

// ---------------------------------------------------------------------------
// Facet-plane flattening
//
// A roof facet is a tilted plane in 3D. Measuring it by dropping Z (top-down
// x,y) shrinks every sloped edge by cos(pitch) — e.g. a real 16'11" edge reads
// ~12'9" on a steep facet. The DXF avoids this by projecting each facet onto an
// in-plane basis (see Files/Calculations/projectSegmentTo2D). We do the same
// here, but ONLY for the per-facet measurement diagram/table — the overview
// site-map still uses raw top-down x,y so segments keep their relative layout.
// ---------------------------------------------------------------------------
const sub3 = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const dot3 = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const cross3 = (a, b) => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});
const len3 = (a) => Math.hypot(a.x, a.y, a.z);
const norm3 = (a) => {
  const l = len3(a) || 1;
  return { x: a.x / l, y: a.y / l, z: a.z / l };
};

const dedupePoints3d = (points = []) => {
  const seen = new Set();
  const out = [];
  points.forEach((p) => {
    const x = Number(p?.x);
    const y = Number(p?.y);
    let z = Number(p?.z);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (!Number.isFinite(z)) z = 0;
    const key = `${x.toFixed(6)}:${y.toFixed(6)}:${z.toFixed(6)}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ x, y, z });
  });
  return out;
};

const getSegmentPoints3d = (polygon) => {
  const edgePoints = Array.isArray(polygon?.edges)
    ? polygon.edges.map((e) => ({ x: e?.point1?.x, y: e?.point1?.y, z: e?.point1?.z }))
    : [];
  const cleanEdges = dedupePoints3d(edgePoints);
  if (cleanEdges.length >= 3) return { points: cleanEdges, ordered: true };

  const pos = polygon?.shape?.geometry?.attributes?.position?.array;
  if (!pos || typeof pos.length !== "number") return { points: [], ordered: false };
  const geoPoints = [];
  for (let i = 0; i < pos.length; i += 3) geoPoints.push({ x: pos[i], y: pos[i + 1], z: pos[i + 2] });
  return { points: dedupePoints3d(geoPoints), ordered: false };
};

const getSegmentObstacles3d = (polygon) => {
  if (!Array.isArray(polygon?.obstructions)) return [];
  return polygon.obstructions
    .map((obs) => {
      const cords = Array.isArray(obs?.cords) ? obs.cords : [];
      const points = cords
        .map((p) => ({ x: Number(p?.x), y: Number(p?.y), z: Number(p?.z) }))
        .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
      const deduped = dedupePoints3d(points);
      return deduped.length >= 3 ? deduped : null;
    })
    .filter(Boolean);
};

const getSegmentSetback3d = (polygon) => {
  const setBacks = polygon?.setBacks;
  if (!setBacks?.isSet || !Array.isArray(setBacks?.edgesData)) return null;
  const points = setBacks.edgesData
    .map((edge) => ({
      x: Number(edge?.point1?.x),
      y: Number(edge?.point1?.y),
      z: Number(edge?.point1?.z),
    }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
    .map((p) => ({ ...p, z: Number.isFinite(p.z) ? p.z : 0 }));
  if (points.length >= 2) {
    const first = points[0];
    const last = points[points.length - 1];
    if (Math.abs(first.x - last.x) < 1e-6 && Math.abs(first.y - last.y) < 1e-6) {
      points.pop();
    }
  }
  return points.length >= 3 ? points : null;
};

// Newell's method: robust plane normal for an arbitrary 3D polygon ring.
const computePlaneNormal = (ring) => {
  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let i = 0; i < ring.length; i++) {
    const cur = ring[i];
    const nxt = ring[(i + 1) % ring.length];
    nx += (cur.y - nxt.y) * (cur.z + nxt.z);
    ny += (cur.z - nxt.z) * (cur.x + nxt.x);
    nz += (cur.x - nxt.x) * (cur.y + nxt.y);
  }
  const n = { x: nx, y: ny, z: nz };
  return len3(n) < 1e-9 ? { x: 0, y: 0, z: 1 } : norm3(n);
};

// Orthonormal in-plane basis (origin, u, v) so that projected 2D distances
// equal the true 3D distances on the facet surface.
const buildPlaneBasis = (ring) => {
  const origin = ring[0];
  const normal = computePlaneNormal(ring);
  let ref = { x: 1, y: 0, z: 0 };
  if (Math.abs(dot3(ref, normal)) > 0.9) ref = { x: 0, y: 1, z: 0 };
  const proj = dot3(ref, normal);
  let u = sub3(ref, { x: normal.x * proj, y: normal.y * proj, z: normal.z * proj });
  u = norm3(u);
  const v = norm3(cross3(normal, u));
  return { origin, u, v };
};

const projectToPlane = (p, basis) => {
  const d = sub3({ x: Number(p?.x), y: Number(p?.y), z: Number.isFinite(Number(p?.z)) ? Number(p.z) : 0 }, basis.origin);
  return { x: dot3(d, basis.u), y: dot3(d, basis.v) };
};

// Flatten a facet (and its obstacles/setback) onto its own plane. Returns
// true-length 2D points in scene-meters; the feet conversion happens later in
// buildFacetMeasurementData. `project` is exposed so callers can flatten ridge
// markers with the same basis.
const flattenFacet = (polygon) => {
  const ring = getSegmentPoints3d(polygon);
  if (ring.points.length < 3) return null;
  const basis = buildPlaneBasis(ring.points);
  const project = (p) => projectToPlane(p, basis);
  let points = ring.points.map(project);
  if (!ring.ordered) points = sortPointsClockwise(points);
  const obstacles = getSegmentObstacles3d(polygon).map((obs) => obs.map(project));
  const setback3d = getSegmentSetback3d(polygon);
  const setback = setback3d ? setback3d.map(project) : null;
  return { points, obstacles, setback, project };
};

const toEdgeKey2d = (edge, decimals = 3) => {
  const a = `${edge.a.x.toFixed(decimals)},${edge.a.y.toFixed(decimals)}`;
  const b = `${edge.b.x.toFixed(decimals)},${edge.b.y.toFixed(decimals)}`;
  return a < b ? `${a}|${b}` : `${b}|${a}`;
};

const dedupeEdgesByKey2d = (edges = []) => {
  const seen = new Set();
  const out = [];
  edges.forEach((edge) => {
    const key = toEdgeKey2d(edge);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(edge);
  });
  return out;
};

const clamp01 = (v) => Math.min(1, Math.max(0, v));

const interpolateEdgePoint = (edge, t) => ({
  x: edge.a.x + (edge.b.x - edge.a.x) * t,
  y: edge.a.y + (edge.b.y - edge.a.y) * t,
  z: edge.a.z + (edge.b.z - edge.a.z) * t,
});

const getCollinearOverlap = (edge1, edge2, { lineTol = 0.08, minOverlap = 0.2 } = {}) => {
  const ax = edge1.a.x;
  const ay = edge1.a.y;
  const bx = edge1.b.x;
  const by = edge1.b.y;
  const cx = edge2.a.x;
  const cy = edge2.a.y;
  const dx = edge2.b.x;
  const dy = edge2.b.y;

  const ux = bx - ax;
  const uy = by - ay;
  const vx = dx - cx;
  const vy = dy - cy;
  const lenU = Math.hypot(ux, uy);
  const lenV = Math.hypot(vx, vy);
  if (lenU < 1e-6 || lenV < 1e-6) return null;

  const crossDir = Math.abs(ux * vy - uy * vx) / (lenU * lenV);
  if (crossDir > 0.02) return null;

  // Distance from edge2 endpoints to edge1 infinite line
  const lineDistC = Math.abs((cx - ax) * uy - (cy - ay) * ux) / lenU;
  const lineDistD = Math.abs((dx - ax) * uy - (dy - ay) * ux) / lenU;
  if (lineDistC > lineTol || lineDistD > lineTol) return null;

  const unitX = ux / lenU;
  const unitY = uy / lenU;
  const proj = (px, py) => (px - ax) * unitX + (py - ay) * unitY;

  const e1Min = 0;
  const e1Max = lenU;
  const cProj = proj(cx, cy);
  const dProj = proj(dx, dy);
  const e2Min = Math.min(cProj, dProj);
  const e2Max = Math.max(cProj, dProj);

  const overlapStart = Math.max(e1Min, e2Min);
  const overlapEnd = Math.min(e1Max, e2Max);
  const overlapLen = overlapEnd - overlapStart;
  if (overlapLen < minOverlap) return null;

  const pStart = { x: ax + unitX * overlapStart, y: ay + unitY * overlapStart };
  const pEnd = { x: ax + unitX * overlapEnd, y: ay + unitY * overlapEnd };

  return { pStart, pEnd };
};

const computeSegmentRidgeMarkers = (edgesByPolygon = []) => {
  const segmentStats = edgesByPolygon.map((edges) => {
    const zs = edges.flatMap((edge) => [edge.a.z, edge.b.z]).filter(Number.isFinite);
    const avgZs = edges.map((edge) => (edge.a.z + edge.b.z) / 2);
    const maxAvgZ = avgZs.length ? Math.max(...avgZs) : -Infinity;
    const minAvgZ = avgZs.length ? Math.min(...avgZs) : Infinity;
    const avgRange = Number.isFinite(maxAvgZ) && Number.isFinite(minAvgZ) ? maxAvgZ - minAvgZ : 0;
    return {
      maxAvgZ,
      tol: Math.max(0.03, avgRange * 0.03),
      flat: avgRange < 0.05 || zs.length < 2,
    };
  });

  const ridgeBySegment = new Map();
  for (let si = 0; si < edgesByPolygon.length; si++) {
    for (let sj = si + 1; sj < edgesByPolygon.length; sj++) {
      const leftStats = segmentStats[si];
      const rightStats = segmentStats[sj];
      if (!leftStats || !rightStats || leftStats.flat || rightStats.flat) continue;

      const leftEdges = edgesByPolygon[si] || [];
      const rightEdges = edgesByPolygon[sj] || [];
      for (let li = 0; li < leftEdges.length; li++) {
        for (let rj = 0; rj < rightEdges.length; rj++) {
          const leftEdge = leftEdges[li];
          const rightEdge = rightEdges[rj];
          const overlap = getCollinearOverlap(leftEdge, rightEdge);
          if (!overlap) continue;

          const leftAvg = (leftEdge.a.z + leftEdge.b.z) / 2;
          const rightAvg = (rightEdge.a.z + rightEdge.b.z) / 2;
          const leftTop = leftAvg >= leftStats.maxAvgZ - leftStats.tol;
          const rightTop = rightAvg >= rightStats.maxAvgZ - rightStats.tol;
          if (!leftTop || !rightTop) continue;

          const leftLen2 = (leftEdge.b.x - leftEdge.a.x) ** 2 + (leftEdge.b.y - leftEdge.a.y) ** 2;
          const rightLen2 =
            (rightEdge.b.x - rightEdge.a.x) ** 2 + (rightEdge.b.y - rightEdge.a.y) ** 2;
          if (leftLen2 < 1e-8 || rightLen2 < 1e-8) continue;

          const leftT1 = clamp01(
            ((overlap.pStart.x - leftEdge.a.x) * (leftEdge.b.x - leftEdge.a.x) +
              (overlap.pStart.y - leftEdge.a.y) * (leftEdge.b.y - leftEdge.a.y)) /
              leftLen2
          );
          const leftT2 = clamp01(
            ((overlap.pEnd.x - leftEdge.a.x) * (leftEdge.b.x - leftEdge.a.x) +
              (overlap.pEnd.y - leftEdge.a.y) * (leftEdge.b.y - leftEdge.a.y)) /
              leftLen2
          );
          const rightT1 = clamp01(
            ((overlap.pStart.x - rightEdge.a.x) * (rightEdge.b.x - rightEdge.a.x) +
              (overlap.pStart.y - rightEdge.a.y) * (rightEdge.b.y - rightEdge.a.y)) /
              rightLen2
          );
          const rightT2 = clamp01(
            ((overlap.pEnd.x - rightEdge.a.x) * (rightEdge.b.x - rightEdge.a.x) +
              (overlap.pEnd.y - rightEdge.a.y) * (rightEdge.b.y - rightEdge.a.y)) /
              rightLen2
          );

          const leftMarker = {
            a: interpolateEdgePoint(leftEdge, Math.min(leftT1, leftT2)),
            b: interpolateEdgePoint(leftEdge, Math.max(leftT1, leftT2)),
          };
          const rightMarker = {
            a: interpolateEdgePoint(rightEdge, Math.min(rightT1, rightT2)),
            b: interpolateEdgePoint(rightEdge, Math.max(rightT1, rightT2)),
          };

          if (!ridgeBySegment.has(si)) ridgeBySegment.set(si, []);
          if (!ridgeBySegment.has(sj)) ridgeBySegment.set(sj, []);
          ridgeBySegment.get(si).push(leftMarker);
          ridgeBySegment.get(sj).push(rightMarker);
        }
      }
    }
  }

  const result = new Map();
  edgesByPolygon.forEach((_edges, idx) => {
    result.set(idx, dedupeEdgesByKey2d(ridgeBySegment.get(idx) || []));
  });
  return result;
};

const getBoundsFromPoints = (points = []) => {
  if (!points.length) return null;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  points.forEach((p) => {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  });
  return {
    minX,
    maxX,
    minY,
    maxY,
    width: Math.max(0.0001, maxX - minX),
    height: Math.max(0.0001, maxY - minY),
  };
};

const getBoundsFromSegments = (segments = []) =>
  getBoundsFromPoints(segments.flatMap((seg) => seg.points));

const rotatePoint = (p, ref, angle) => {
  const dx = p.x - ref.x;
  const dy = p.y - ref.y;
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return {
    x: ref.x + dx * c - dy * s,
    y: ref.y + dx * s + dy * c,
  };
};

// Scene geometry is stored in meters (same as the DXF source); the report
// labels everything in feet. The DXF path converts via `point * 3.281`
// (see Files/Dxf/DxfCalculations.js); the facet report must apply the same
// conversion or every dimension reads ~3.281x too small.
const FEET_PER_METER = 3.281;
const scaleToFeet = (p) => {
  if (!p) return p;
  const out = { ...p, x: Number(p.x) * FEET_PER_METER, y: Number(p.y) * FEET_PER_METER };
  if (Number.isFinite(Number(p.z))) out.z = Number(p.z) * FEET_PER_METER;
  return out;
};

const buildFacetMeasurementData = (points = [], highestEdges = [], obstacles = [], setback = null) => {
  if (!points.length) {
    return {
      alignedPoints: [],
      alignedHighestEdges: [],
      alignedObstacles: [],
      alignedSetback: null,
      rows: [],
    };
  }
  // Convert all incoming geometry from scene-meters to feet before alignment.
  // Alignment is a rigid rotation+translation, so scaling first preserves the
  // result while ensuring the diagram and the coordinate table read in feet.
  points = points.map(scaleToFeet);
  highestEdges = (Array.isArray(highestEdges) ? highestEdges : []).map((edge) =>
    edge && edge.a && edge.b ? { ...edge, a: scaleToFeet(edge.a), b: scaleToFeet(edge.b) } : edge
  );
  obstacles = (Array.isArray(obstacles) ? obstacles : []).map((obs) =>
    Array.isArray(obs) ? obs.map(scaleToFeet) : obs
  );
  setback = Array.isArray(setback) ? setback.map(scaleToFeet) : setback;
  const ref = points[0];
  const second = points[1] || points[0];
  const angle = -Math.atan2(second.y - ref.y, second.x - ref.x);
  const alignedPoints = points.map((p) => rotatePoint(p, ref, angle));
  const alignedHighestEdges = (Array.isArray(highestEdges) ? highestEdges : [])
    .map((edge) => {
      const a = edge?.a;
      const b = edge?.b;
      const valid =
        Number.isFinite(a?.x) &&
        Number.isFinite(a?.y) &&
        Number.isFinite(b?.x) &&
        Number.isFinite(b?.y);
      if (!valid) return null;
      return {
        a: rotatePoint(a, ref, angle),
        b: rotatePoint(b, ref, angle),
      };
    })
    .filter(Boolean);
  const alignedObstacles = (Array.isArray(obstacles) ? obstacles : [])
    .map((obsPoints) => {
      if (!Array.isArray(obsPoints) || obsPoints.length < 3) return null;
      const aligned = obsPoints
        .map((p) => {
          if (!Number.isFinite(p?.x) || !Number.isFinite(p?.y)) return null;
          return rotatePoint(p, ref, angle);
        })
        .filter(Boolean);
      return aligned.length >= 3 ? aligned : null;
    })
    .filter(Boolean);
  const alignedSetback =
    Array.isArray(setback) && setback.length >= 3
      ? setback
          .map((p) => {
            if (!Number.isFinite(p?.x) || !Number.isFinite(p?.y)) return null;
            return rotatePoint(p, ref, angle);
          })
          .filter(Boolean)
      : null;

  const rp = alignedPoints[0];
  const rows = [{ poi: "RP", x: "0'0''", y: "0'0''", len: "-", wid: "-" }];
  for (let i = 1; i < alignedPoints.length; i++) {
    const p = alignedPoints[i];
    const dx = Number(Math.abs(p.x - rp.x).toFixed(2));
    const dy = Number(Math.abs(p.y - rp.y).toFixed(2));
    rows.push({
      poi: String(i),
      x: formatDistanceFtIn(dx),
      y: formatDistanceFtIn(dy),
      len: "-",
      wid: "-",
    });
  }

  alignedHighestEdges.forEach((edge, idx) => {
    const mx = (edge.a.x + edge.b.x) / 2;
    const my = (edge.a.y + edge.b.y) / 2;
    const dx = Math.abs(mx - rp.x);
    const dy = Math.abs(my - rp.y);
    const len = Math.hypot(edge.b.x - edge.a.x, edge.b.y - edge.a.y);
    rows.push({
      poi: `R${idx + 1}`,
      x: formatDistanceFtIn(dx),
      y: formatDistanceFtIn(dy),
      len: formatDistanceFtIn(len),
      wid: "-",
    });
  });

  alignedObstacles.forEach((obs, idx) => {
    const bounds = getBoundsFromPoints(obs);
    if (!bounds) return;
    const cx = obs.reduce((sum, p) => sum + p.x, 0) / obs.length;
    const cy = obs.reduce((sum, p) => sum + p.y, 0) / obs.length;
    const dx = Math.abs(cx - rp.x);
    const dy = Math.abs(cy - rp.y);
    rows.push({
      poi: `O${idx + 1}`,
      x: formatDistanceFtIn(dx),
      y: formatDistanceFtIn(dy),
      len: formatDistanceFtIn(bounds.width),
      wid: formatDistanceFtIn(bounds.height),
    });
  });

  return { alignedPoints, alignedHighestEdges, alignedObstacles, alignedSetback, rows };
};

const normalizeFacetReportData = async (currentDesign, reportData = {}, flightInfoAspectRatio = 1) => {
  const flight = {
    ...DEFAULT_FLIGHT,
    ...(reportData?.flight || currentDesign?.facetReport?.flight || {}),
  };
  const altitudes = Array.isArray(reportData?.altitudes)
    ? reportData.altitudes
    : Array.isArray(currentDesign?.facetReport?.altitudes)
    ? currentDesign.facetReport.altitudes
    : buildDynamicFacetAltitudes(currentDesign);

  const rawMapTileImage =
    reportData?.mapTileImage ||
    VARS?.mapTile?.material?.map?.image?.currentSrc ||
    VARS?.mapTile?.material?.map?.image?.src ||
    currentDesign?.facetReport?.mapTileImage ||
    "";

  let mapTileImage = rawMapTileImage;
  const currentMapDimensions = rawMapTileImage ? await loadImageDimensions(rawMapTileImage) : null;
  const currentAspectRatio =
    currentMapDimensions?.width && currentMapDimensions?.height
      ? currentMapDimensions.width / currentMapDimensions.height
      : null;
  if (
    !currentAspectRatio ||
    Math.abs(currentAspectRatio - flightInfoAspectRatio) > 0.02
  ) {
    const replacementMap = await buildFlightInfoMapImage(currentDesign, flightInfoAspectRatio);
    if (replacementMap) mapTileImage = replacementMap;
  }

  return { flight, altitudes, mapTileImage };
};

const getPdfImageType = (imageSource) => {
  if (typeof imageSource === "string" && imageSource.startsWith("data:image/jpeg")) {
    return "JPEG";
  }
  return "PNG";
};

const drawHeader = (
  doc,
  { pageWidth, left, right, customerName, customerAddress, logoDataUrl, logoPlacement }
) => {
  const headerY = 26;
  doc.setFillColor(...colors.bg);
  doc.rect(0, 0, pageWidth, 792, "F");

  try {
    const logoSource = logoDataUrl || sflLogo;
    const placement = logoPlacement || { x: left + 1, y: headerY + 4, width: 112, height: 20 };
    doc.addImage(
      logoSource,
      getPdfImageType(logoSource),
      placement.x,
      placement.y,
      placement.width,
      placement.height
    );
  } catch (e) {}

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5);
  doc.setTextColor(...colors.text);
  doc.text(customerName, pageWidth - right, headerY + 9, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.text(customerAddress, pageWidth - right, headerY + 22, { align: "right" });

  doc.setDrawColor(...colors.border);
  doc.setLineWidth(1);
  doc.line(left, 66, pageWidth - right, 66);
};

const drawFooter = (
  doc,
  { page, pageWidth, pageHeight, left, right, designer }
) => {
  const footerTop = pageHeight - 44;
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(1);
  doc.line(left, footerTop, pageWidth - right, footerTop);

  try {
    doc.addImage(sflLogo, "PNG", left + 1, footerTop + 11, 62, 11);
  } catch (e) {}

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...colors.text);
  doc.text(`Page ${page}`, pageWidth / 2, footerTop + 22, { align: "center" });

  // Draw the designer name right-aligned at the margin, then place the
  // "Designer:" label immediately to its left based on the measured name
  // width, so a long name can never overlap the label.
  const designerText = designer || "N/A";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...colors.text);
  const nameWidth = doc.getTextWidth(designerText);
  doc.text(designerText, pageWidth - right, footerTop + 22, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...colors.muted);
  doc.text("Designer:", pageWidth - right - nameWidth - 6, footerTop + 22, { align: "right" });
};

const drawRoundedCard = (doc, x, y, w, h, radius = 10, fill = true) => {
  doc.setDrawColor(...colors.border);
  if (fill) doc.setFillColor(...colors.card);
  doc.roundedRect(x, y, w, h, radius, radius, fill ? "FD" : "S");
};

const buildProjector = (bounds, x, y, w, h, pad = 10) => {
  const usableW = Math.max(1, w - pad * 2);
  const usableH = Math.max(1, h - pad * 2);
  const sx = usableW / Math.max(0.0001, bounds.width || 1);
  const sy = usableH / Math.max(0.0001, bounds.height || 1);
  const s = Math.min(sx, sy);
  const drawW = Math.max(1, (bounds.width || 1) * s);
  const drawH = Math.max(1, (bounds.height || 1) * s);
  const ox = x + (w - drawW) / 2;
  const oy = y + (h - drawH) / 2;
  return (p) => ({
    px: ox + (p.x - bounds.minX) * s,
    py: oy + drawH - (p.y - bounds.minY) * s,
  });
};

const drawPolygonLines = (doc, points, projector, lineWidth = 1, color = colors.wire) => {
  doc.setDrawColor(...color);
  doc.setLineWidth(lineWidth);
  for (let i = 0; i < points.length; i++) {
    const a = projector(points[i]);
    const b = projector(points[(i + 1) % points.length]);
    doc.line(a.px, a.py, b.px, b.py);
  }
};

const fillPolygon = (doc, points, projector, fillColor) => {
  if (!Array.isArray(points) || points.length < 3) return;
  const projected = points.map((p) => projector(p));
  const first = projected[0];
  const relLines = [];
  let prev = first;
  for (let i = 1; i < projected.length; i++) {
    const curr = projected[i];
    relLines.push([curr.px - prev.px, curr.py - prev.py]);
    prev = curr;
  }
  relLines.push([first.px - prev.px, first.py - prev.py]);
  doc.setFillColor(...fillColor);
  doc.lines(relLines, first.px, first.py, [1, 1], "F", false);
};

const drawHighestEdgeMarkers = (
  doc,
  edges,
  projector,
  { color = colors.highestMarker, offset = 2.6, width = 0.9 } = {}
) => {
  if (!Array.isArray(edges) || !edges.length) return;
  doc.setDrawColor(...color);
  doc.setLineWidth(width);

  edges.forEach((edge) => {
    const ap = projector(edge.a);
    const bp = projector(edge.b);
    const dx = bp.px - ap.px;
    const dy = bp.py - ap.py;
    const len = Math.hypot(dx, dy);
    if (len < 2) return;

    const nx = -dy / len;
    const ny = dx / len;
    const a1 = { x: ap.px + nx * offset, y: ap.py + ny * offset };
    const b1 = { x: bp.px + nx * offset, y: bp.py + ny * offset };
    const a2 = { x: ap.px - nx * offset, y: ap.py - ny * offset };
    const b2 = { x: bp.px - nx * offset, y: bp.py - ny * offset };

    doc.line(a1.x, a1.y, b1.x, b1.y);
    doc.line(a2.x, a2.y, b2.x, b2.y);
    doc.line(a1.x, a1.y, a2.x, a2.y);
    doc.line(b1.x, b1.y, b2.x, b2.y);
  });
};

const drawObstaclePolygons = (
  doc,
  obstacles,
  projector,
  { lineWidth = 0.9, strokeColor = colors.obstacle, fillColor = colors.obstacle, fillOpacity = 0.12 } = {}
) => {
  if (!Array.isArray(obstacles) || !obstacles.length) return;
  const fill = fillColor.map((c) => Math.round(255 - (255 - c) * fillOpacity));
  doc.setDrawColor(...strokeColor);
  doc.setFillColor(...fill);
  doc.setLineWidth(lineWidth);
  obstacles.forEach((points) => {
    if (!Array.isArray(points) || points.length < 3) return;
    fillPolygon(doc, points, projector, fill);
    drawPolygonLines(doc, points, projector, lineWidth, strokeColor);
  });
};

const drawSetbackBand = (
  doc,
  segmentPoints,
  setbackPoints,
  projector,
  { lineWidth = 0.9, strokeColor = colors.setback, fillColor = colors.setback, fillOpacity = 0.18 } = {}
) => {
  if (!Array.isArray(segmentPoints) || segmentPoints.length < 3) return;
  if (!Array.isArray(setbackPoints) || setbackPoints.length < 3) return;

  const n = segmentPoints.length;
  if (setbackPoints.length !== n) {
    // Fallback: keep only setback outline if geometry doesn't map 1:1 to segment edges.
    drawPolygonLines(doc, setbackPoints, projector, lineWidth, strokeColor);
    return;
  }

  const fill = fillColor.map((c) => Math.round(255 - (255 - c) * fillOpacity));
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const quad = [segmentPoints[i], segmentPoints[j], setbackPoints[j], setbackPoints[i]];
    fillPolygon(doc, quad, projector, fill);
  }
  drawPolygonLines(doc, setbackPoints, projector, lineWidth, strokeColor);
};

const drawMiniMap = (
  doc,
  allSegments,
  selectedIdx,
  x,
  y,
  w,
  h,
  {
    showSelectedOutline = true,
    selectedColor = colors.wire,
    selectedLineWidth = 2,
    lineWidth = 1,
    fillSelected = false,
    selectedFillColor = [232, 236, 240],
    showLabels = false,
    labelColor = colors.text,
    labelSize = 8,
    showHighestEdgeMarkers = true,
  } = {}
) => {
  const bounds = getBoundsFromSegments(allSegments);
  if (!bounds) return;
  const projector = buildProjector(bounds, x, y, w, h, 10);
  allSegments.forEach((seg, idx) => {
    if (!showSelectedOutline && idx === selectedIdx) return;
    if (fillSelected && idx === selectedIdx) {
      fillPolygon(doc, seg.points, projector, selectedFillColor);
    }
    const color = idx === selectedIdx ? selectedColor : colors.wire;
    drawPolygonLines(doc, seg.points, projector, idx === selectedIdx ? selectedLineWidth : lineWidth, color);
    drawSetbackBand(doc, seg.points, seg.setback, projector, { lineWidth: 0.8 });
    drawObstaclePolygons(doc, seg.obstacles, projector, { lineWidth: 0.8 });
    if (showHighestEdgeMarkers) {
      drawHighestEdgeMarkers(doc, seg.highestEdges, projector);
    }
    if (showLabels && Array.isArray(seg.points) && seg.points.length) {
      const cx = seg.points.reduce((sum, p) => sum + p.x, 0) / seg.points.length;
      const cy = seg.points.reduce((sum, p) => sum + p.y, 0) / seg.points.length;
      const c = projector({ x: cx, y: cy });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelSize);
      doc.setTextColor(...labelColor);
      doc.text(seg.label || String(idx + 1), c.px, c.py, { align: "center" });
    }
  });
};

const drawSegmentInSmallBox = (doc, seg, x, y, w, h) => {
  if (!seg?.points?.length) return;
  const bounds = getBoundsFromPoints(seg.points);
  if (!bounds) return;
  const projector = buildProjector(bounds, x, y, w, h, 4);
  drawPolygonLines(doc, seg.points, projector, 1, colors.wire);
  drawSetbackBand(doc, seg.points, seg.setback, projector, { lineWidth: 0.7 });
  drawObstaclePolygons(doc, seg.obstacles, projector, { lineWidth: 0.7 });
  drawHighestEdgeMarkers(doc, seg.highestEdges, projector, { offset: 2.1, width: 0.8 });
};

const drawNorthIndicator = async (doc, x, y, size = 28) => {
  if (!compassIconDataUrl) return;
  const h = size;
  const w = Math.max(1, h * COMPASS_ASPECT);
  doc.addImage(compassIconDataUrl, "PNG", x, y, w, h, "compass-icon", "NONE");
};

const drawAltitudeRows = (doc, { x, y, w, h, rows }) => {
  const topPad = 12;
  const bottomPad = 10;
  const rowH = (h - topPad - bottomPad) / Math.max(1, rows.length);
  let cy = y + topPad;

  rows.forEach((row, idx) => {
    const label = row?.label || "-";
    const value = row?.value || "-";

    if (idx > 0) {
      doc.setDrawColor(...colors.border);
      doc.setLineWidth(0.8);
      doc.line(x + 12, cy - 4, x + w - 12, cy - 4);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(px(10));
    doc.setTextColor(...colors.muted);
    doc.text(label, x + 14, cy + rowH * 0.45);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(px(12));
    doc.setTextColor(...colors.text);
    doc.text(value, x + w - 14, cy + rowH * 0.45, { align: "right" });

    cy += rowH;
  });
};

const drawFacetMeasurementDiagram = (
  doc,
  { points, highestEdges = [], obstacles = [], setback = null, x, y, w, h }
) => {
  const bounds = getBoundsFromPoints(points);
  if (!bounds) return;

  drawRoundedCard(doc, x, y, w, h, 14, true);
  const contentX = x + 20;
  const contentY = y + 20;
  const contentW = w - 40;
  const contentH = h - 40;

  const rp = points[0];
  const EPS = 0.005;
  const MIN_DIM_FT = 1;
  const DIMENSION_CLUSTER_TOL_FT = 6 / 12;
  const clusterDimensionEntries = (entries, toleranceFt) => {
    if (!Array.isArray(entries) || !entries.length) return [];
    const sorted = [...entries].sort((a, b) => a.value - b.value);
    const clusters = [];

    sorted.forEach((entry) => {
      const last = clusters[clusters.length - 1];
      if (!last || entry.value - last.value > toleranceFt) {
        clusters.push({ ...entry });
        return;
      }

      // Keep the larger representative value so the displayed dimension does
      // not understate the facet extent when two offsets are nearly identical.
      if (entry.value >= last.value) {
        last.value = entry.value;
        last.point = entry.point;
      }
    });

    return clusters;
  };

  const xCandidates = [];
  const yCandidates = [];
  points.forEach((p, idx) => {
    if (idx === 0) return;
    const dx = Math.abs(p.x - rp.x);
    const dy = Math.abs(p.y - rp.y);
    if (dx + EPS >= MIN_DIM_FT) {
      xCandidates.push({ value: dx, point: p });
    }
    if (dy + EPS >= MIN_DIM_FT) {
      yCandidates.push({ value: dy, point: p });
    }
  });
  const xEntries = clusterDimensionEntries(xCandidates, DIMENSION_CLUSTER_TOL_FT);
  const yEntries = clusterDimensionEntries(yCandidates, DIMENSION_CLUSTER_TOL_FT);

  let xStep = xEntries.length > 2 ? 18 : 14;
  let yStep = yEntries.length > 1 ? 22 : 16;
  const bottomReserve = 12;

  let drawX;
  let drawY;
  let drawW;
  let drawH;

  const recompute = () => {
    const leftReserve = 28 + Math.max(0, xEntries.length - 1) * 6;
    const topReserve = 14 + xEntries.length * xStep;
    const rightReserve = 40 + yEntries.length * yStep;
    drawX = contentX + leftReserve;
    drawY = contentY + topReserve;
    drawW = contentW - leftReserve - rightReserve;
    drawH = contentH - topReserve - bottomReserve;
  };

  recompute();
  while ((drawW < 170 || drawH < 90) && (xStep > 8 || yStep > 8)) {
    if (drawW < 170 && yStep > 8) yStep -= 1;
    if (drawH < 90 && xStep > 8) xStep -= 1;
    recompute();
  }
  drawW = Math.max(140, drawW);
  drawH = Math.max(80, drawH);
  const projector = buildProjector(bounds, drawX, drawY, drawW, drawH, 10);
  const rpProj = projector(rp);

  drawPolygonLines(doc, points, projector, 1.2, colors.dimension);
  drawSetbackBand(doc, points, setback, projector, { lineWidth: 0.85 });
  drawObstaclePolygons(doc, obstacles, projector, { lineWidth: 0.85 });
  drawHighestEdgeMarkers(doc, highestEdges, projector);

  doc.setDrawColor(...colors.dimensionLight);
  doc.setLineWidth(0.9);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...colors.dimensionLight);

  const topBase = drawY - 8;
  const rightBase = drawX + drawW + 16;

  xEntries.forEach((entry, level) => {
    const pt = projector(entry.point);
    const topY = Math.max(contentY + 2, topBase - level * xStep);
    const nearVertical = Math.abs(pt.px - rpProj.px) < 12;
    const dxText = formatDistanceFtIn(entry.value);
    if (nearVertical) {
      const baseOut = Math.max(contentX + 2, drawX - 8 - level * 7);
      const rpOutX = baseOut;
      const ptOutX = Math.max(contentX + 2, baseOut - 12);
      doc.line(rpProj.px, rpProj.py, rpOutX, rpProj.py);
      doc.line(rpOutX, rpProj.py, rpOutX, topY);
      doc.line(pt.px, pt.py, ptOutX, pt.py);
      doc.line(ptOutX, pt.py, ptOutX, topY);
      doc.line(ptOutX, topY, rpOutX, topY);
      doc.text(dxText, ((ptOutX + rpOutX) / 2) - 2, topY - 2);
    } else {
      doc.line(pt.px, pt.py, pt.px, topY);
      doc.line(rpProj.px, rpProj.py, rpProj.px, topY);
      doc.line(rpProj.px, topY, pt.px, topY);
      doc.text(dxText, (rpProj.px + pt.px) / 2 + 2, topY - 2);
    }
  });

  yEntries.forEach((entry, level) => {
    const pt = projector(entry.point);
    const rightX = Math.min(contentX + contentW - 4, rightBase + level * yStep);
    const dyText = formatDistanceFtIn(entry.value);
    doc.line(pt.px, pt.py, rightX, pt.py);
    doc.line(rpProj.px, rpProj.py, rightX, rpProj.py);
    doc.line(rightX, rpProj.py, rightX, pt.py);
    doc.text(dyText, rightX + 2, (rpProj.py + pt.py) / 2);
  });

  points.forEach((p, idx) => {
    if (idx === 0) return;
    const pt = projector(p);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...colors.wire);
    doc.text(String(idx), pt.px + 3, pt.py - 3);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...colors.dimensionLight);
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...colors.wire);
  doc.text("RP", rpProj.px + 4, rpProj.py - 4);
};

const drawFacetDataTable = (doc, { x, y, w, rows, maxH }) => {
  const headerH = 24;
  const subHeaderH = 22;
  const rowH = 22;
  const safeMaxH = Math.max(headerH + subHeaderH + rowH, maxH || Infinity);
  const maxRowsByHeight = Math.max(
    1,
    Math.floor((safeMaxH - headerH - subHeaderH) / rowH)
  );
  const tableCount = Math.max(1, Math.ceil(rows.length / maxRowsByHeight));
  const gapX = tableCount > 1 ? 8 : 0;
  const singleW = (w - gapX * (tableCount - 1)) / tableCount;
  const bodyH = Math.max(rowH, Math.min(maxRowsByHeight, rows.length || 1) * rowH);
  const tableH = headerH + subHeaderH + bodyH;

  doc.setFont("helvetica", "normal");
  const getPoiRowColor = (poi) => {
    const key = String(poi || "").toUpperCase();
    if (/^R\d+$/.test(key)) return colors.highestMarker;
    if (/^O\d+$/.test(key)) return colors.obstacle;
    return colors.text;
  };

  for (let t = 0; t < tableCount; t++) {
    const tx = x + t * (singleW + gapX);
    const tableRows = rows.slice(t * maxRowsByHeight, (t + 1) * maxRowsByHeight);
    const localBodyH = Math.max(rowH, tableRows.length * rowH);
    const localTableH = headerH + subHeaderH + localBodyH;

    doc.setFillColor(...colors.deep);
    doc.setDrawColor(...colors.deep);
    doc.roundedRect(tx, y, singleW, headerH, 10, 10, "FD");
    doc.rect(tx, y + headerH - 10, singleW, 10, "F");

    const compact = singleW < 235;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(compact ? px(8.5) : px(10));
    doc.setTextColor(255, 255, 255);
    doc.text("Point of Interest", tx + 8, y + 15);
    doc.text(compact ? "Coordinates" : "Coordinates & Dimension Data", tx + singleW - 8, y + 15, {
      align: "right",
    });

    const colRatios = [0.16, 0.2, 0.2, 0.22, 0.22];
    const colW = colRatios.map((r) => singleW * r);
    let cx = tx;
    const headers = ["POI", "X", "Y", "LEN", "WID"];
    doc.setFillColor(236, 240, 244);
    doc.setDrawColor(...colors.border);
    doc.rect(tx, y + headerH, singleW, subHeaderH, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(compact ? px(8.5) : px(10));
    doc.setTextColor(...colors.muted);
    headers.forEach((hd, idx) => {
      doc.text(hd, cx + 6, y + headerH + 15);
      cx += colW[idx];
    });

    const bodyY = y + headerH + subHeaderH;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...colors.border);
    doc.roundedRect(tx, bodyY, singleW, localBodyH, 10, 10, "FD");
    doc.rect(tx, bodyY, singleW, Math.max(0, localBodyH - 10), "F");

    let cy = bodyY;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(compact ? px(10) : px(12));
    tableRows.forEach((row, rowIdx) => {
      if (rowIdx > 0) {
        doc.setDrawColor(...colors.border);
        doc.setLineWidth(0.7);
        doc.line(tx, cy, tx + singleW, cy);
      }
      cx = tx;
      const rowColor = getPoiRowColor(row.poi);
      doc.setTextColor(...rowColor);
      [row.poi, row.x, row.y, row.len, row.wid].forEach((cell, idx) => {
        doc.text(String(cell), cx + 6, cy + 15);
        cx += colW[idx];
      });
      cy += rowH;
    });
  }

  return tableH;
};

const drawOverviewPage = ({
  doc,
  pageNumber,
  overviewPageNumber,
  overviewPageTotal,
  allSegments,
  segmentsForBoxes,
  detailPageStart,
  sharedCtx,
}) => {
  const { left, pageWidth, pageHeight, right } = sharedCtx;
  const contentW = pageWidth - left - right;
  drawHeader(doc, sharedCtx);

  const innerX = left;
  const innerW = contentW;
  let y = 84;
  drawRoundedCard(doc, innerX, y, innerW, 46, 12, true);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(px(16));
  doc.setTextColor(...colors.text);
  doc.text("Roof Sections Overview", innerX + 12, y + 28);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(px(14));
  doc.setTextColor(...colors.muted);
  doc.text(`Content ${overviewPageNumber} of ${overviewPageTotal}`, innerX + innerW - 12, y + 28, {
    align: "right",
  });
  y += 52;

  const mapH = 300;
  drawRoundedCard(doc, innerX, y, innerW, mapH, 14, true);
  drawMiniMap(doc, allSegments, -1, innerX + 20, y + 20, innerW - 40, mapH - 40, {
    showLabels: true,
    labelSize: 7.5,
    labelColor: colors.focus,
  });
  y += mapH + 18;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(px(16));
  doc.setTextColor(...colors.text);
  doc.text("Sections", innerX + 4, y);
  y += 10;

  const cols = 6;
  const rows = 4;
  const gapX = 8;
  const gapY = 6;
  const cardW = (innerW - gapX * (cols - 1)) / cols;
  const cardH = 60;
  const maxCards = cols * rows;
  segmentsForBoxes.slice(0, maxCards).forEach((seg, idx) => {
    const r = Math.floor(idx / cols);
    const c = idx % cols;
    const x = innerX + c * (cardW + gapX);
    const cy = y + r * (cardH + gapY);
    drawRoundedCard(doc, x, cy, cardW, cardH, 12, true);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(px(10));
    doc.setTextColor(...colors.text);
    doc.text(seg.label, x + 8, cy + 14);
    drawSegmentInSmallBox(doc, seg, x + 5, cy + 17, cardW - 10, cardH - 20);
    const targetPage = detailPageStart + (seg.globalIndex || 0);
    doc.link(x, cy, cardW, cardH, { pageNumber: targetPage });
  });

  drawFooter(doc, {
    ...sharedCtx,
    page: pageNumber,
    pageWidth,
    pageHeight,
    designer: sharedCtx.designerName,
  });
};

const drawFacetDetailPage = async ({
  doc,
  pageNumber,
  segment,
  allSegments,
  segmentIdx,
  sharedCtx,
}) => {
  const { left, pageWidth, pageHeight, right } = sharedCtx;
  const contentW = pageWidth - left - right;
  drawHeader(doc, sharedCtx);

  let y = 84;
  drawRoundedCard(doc, left, y, contentW, 52, 14, true);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(px(20));
  doc.setTextColor(...colors.text);
  doc.text(`Section ${segment.label}`, left + 12, y + 32);

  const stats = [
    { label: "Pitch", value: `${toFixedOrDash(segment.pitch, 0)}°` },
    { label: "Area", value: `${toFixedOrDash(segment.area, 0)} ft²` },
    { label: "Azimuth", value: `${toFixedOrDash(segment.azimuth, 0)}°` },
  ];
  let statX = left + contentW - 16;
  for (let i = stats.length - 1; i >= 0; i--) {
    const { label, value } = stats[i];
    doc.setFont("helvetica", "normal");
    doc.setFontSize(px(12));
    doc.setTextColor(...colors.muted);
    const labelW = doc.getTextWidth(`${label} `);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(px(12));
    doc.setTextColor(...colors.text);
    const valueW = doc.getTextWidth(value);
    doc.text(value, statX, y + 32, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(px(12));
    doc.setTextColor(...colors.muted);
    doc.text(label, statX - valueW - 4, y + 32, { align: "right" });
    statX -= labelW + valueW + 10;
  }
  y += 62;

  const topH = 300;
  // Use facet-plane flattened geometry so sloped edges read their true length.
  // Falls back to top-down points if the plane could not be built.
  const flat = segment.flat && segment.flat.points.length >= 3 ? segment.flat : null;
  const measurementData = buildFacetMeasurementData(
    flat ? flat.points : segment.points,
    flat
      ? (segment.highestEdges || []).map((e) => ({ a: flat.project(e.a), b: flat.project(e.b) }))
      : segment.highestEdges,
    flat ? flat.obstacles : segment.obstacles,
    flat ? flat.setback : segment.setback
  );
  drawFacetMeasurementDiagram(doc, {
    points: measurementData.alignedPoints,
    highestEdges: measurementData.alignedHighestEdges,
    obstacles: measurementData.alignedObstacles,
    setback: measurementData.alignedSetback,
    x: left,
    y,
    w: contentW,
    h: topH,
  });
  y += topH + 14;

  const leftW = 210;
  const tableX = left + leftW + 12;
  const tableW = contentW - leftW - 12;
  const maxLowerH = Math.max(120, pageHeight - 58 - y);
  const tableH = drawFacetDataTable(doc, {
    x: tableX,
    y,
    w: tableW,
    rows: measurementData.rows,
    maxH: maxLowerH,
  });

  const lowerH = tableH;
  drawRoundedCard(doc, left, y, leftW, lowerH, 12, true);
  const compassH = 28;
  const compassW = compassH * (26 / 40);
  await drawNorthIndicator(doc, left + leftW - 10 - compassW, y + 10, compassH);
  drawMiniMap(doc, allSegments, segmentIdx, left + 10, y + 10, leftW - 20, lowerH - 20, {
    showSelectedOutline: true,
    selectedColor: colors.wire,
    selectedLineWidth: 1.2,
    lineWidth: 1,
    fillSelected: true,
    selectedFillColor: [226, 230, 235],
  });

  drawFooter(doc, {
    ...sharedCtx,
    page: pageNumber,
    pageWidth,
    pageHeight,
    designer: sharedCtx.designerName,
  });
};

const drawFlightInfoPage = async ({
  doc,
  pageNumber,
  sharedCtx,
  mapTileImage,
  flight,
  altitudes,
}) => {
  const { left, pageWidth, pageHeight, right } = sharedCtx;
  const contentW = pageWidth - left - right;
  drawHeader(doc, sharedCtx);
  let currentPage = pageNumber;

  let y = 126;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(px(16));
  doc.setTextColor(...colors.text);
  doc.text("Flight Information", left, y);
  y += 12;

  const imgW = contentW;
  const imgH = FLIGHT_INFO_IMAGE_HEIGHT;
  const imgX = left;
  const imgY = y;
  drawRoundedCard(doc, imgX, imgY, imgW, imgH, 14, true);
  let imageDrawn = false;
  if (mapTileImage) {
    try {
      doc.addImage(mapTileImage, getPdfImageType(mapTileImage), imgX, imgY, imgW, imgH);
      imageDrawn = true;
    } catch (e) {}
    if (!imageDrawn) {
      try {
        doc.addImage(
          mapTileImage,
          getPdfImageType(mapTileImage) === "JPEG" ? "PNG" : "JPEG",
          imgX,
          imgY,
          imgW,
          imgH
        );
        imageDrawn = true;
      } catch (e) {}
    }
  }
  if (!imageDrawn) {
    doc.setFillColor(222, 230, 224);
    doc.rect(imgX, imgY, imgW, imgH, "F");
  }
  y += imgH + 24;

  const infoCards = [
    ["Pilot", flight.pilot],
    ["Drone ID", flight.droneId],
    ["Scan Date", flight.scanDate],
    ["Start", flight.start],
    ["End", flight.end],
    ["Duration", flight.duration],
  ];
  const cols = 3;
  const cardGap = 8;
  const cardW = (contentW - cardGap * (cols - 1)) / cols;
  const cardH = 60;
  const infoBlockH = cardH * 2 + cardGap;
  const altitudeTitleGap = 14;
  const altitudeTitleH = 8;
  const altitudeH = 112;
  const footerTop = pageHeight - 44;
  const requiredAfterGeneralInfoTitle = 10 + infoBlockH + altitudeTitleGap + altitudeTitleH + altitudeH;

  if (y + requiredAfterGeneralInfoTitle > footerTop) {
    drawFooter(doc, {
      ...sharedCtx,
      page: currentPage,
      pageWidth,
      pageHeight,
      designer: sharedCtx.designerName,
    });
    doc.addPage();
    currentPage += 1;
    drawHeader(doc, sharedCtx);
    y = 126;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(px(14));
  doc.setTextColor(...colors.text);
  doc.text("General Info", left, y);
  y += 10;

  const rowW = cardW * cols + cardGap * (cols - 1);
  const rowStartX = left + (contentW - rowW) / 2;
  infoCards.forEach((info, idx) => {
    const r = Math.floor(idx / cols);
    const c = idx % cols;
    const cx = rowStartX + c * (cardW + cardGap);
    const cy = y + r * (cardH + cardGap);
    drawRoundedCard(doc, cx, cy, cardW, cardH, 12, true);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(px(10));
    doc.setTextColor(...colors.muted);
    doc.text(info[0], cx + cardW / 2, cy + 25, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(px(12));
    doc.setTextColor(...colors.text);
    doc.text(info[1] || "-", cx + cardW / 2, cy + 40, { align: "center" });
  });
  y += infoBlockH + altitudeTitleGap;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(px(14));
  doc.setTextColor(...colors.text);
  doc.text("Altitudes", left, y);
  y += 8;

  drawRoundedCard(doc, left, y, contentW, altitudeH, 12, true);
  drawAltitudeRows(doc, {
    x: left,
    y,
    w: contentW,
    h: altitudeH,
    rows: altitudes.slice(0, 4).map((a) => ({
      label: a?.label || "-",
      value: a?.value || "-",
    })),
  });

  drawFooter(doc, {
    ...sharedCtx,
    page: currentPage,
    pageWidth,
    pageHeight,
    designer: sharedCtx.designerName,
  });
};

export async function generateFacetReport(currentDesign, reportData = {}) {
  compassIconDataUrl = "";
  compassIconDataUrl = await loadCompassIconData();
  const organizationLogoUrl = getPreferredOrganizationLogoUrl(currentDesign);
  const rawOrganizationLogoDataUrl = await loadImageAsDataUrl(organizationLogoUrl);
  const organizationLogoDataUrl = rawOrganizationLogoDataUrl
    ? await trimImageWhitespace(rawOrganizationLogoDataUrl)
    : "";
  const logoSource = organizationLogoDataUrl || sflLogo;
  const logoDimensions = await loadImageDimensions(logoSource);
  const polygons = Array.isArray(POLYGONS) ? POLYGONS : [];
  const edgesByPolygon = polygons.map((polygon) => getSegmentEdges3d(polygon));
  const ridgeMarkersBySegment = computeSegmentRidgeMarkers(edgesByPolygon);

  const rawSegments = polygons
    .map((polygon, index) => ({
      label: getSegmentLabel(polygon, index),
      points: getSegmentPoints2d(polygon),
      // Facet-plane flattened geometry (true sloped lengths) for the per-facet
      // measurement diagram/table. `points` above stays top-down for the overview.
      flat: flattenFacet(polygon),
      highestEdges: ridgeMarkersBySegment.get(index) || [],
      obstacles: getSegmentObstacles2d(polygon),
      setback: getSegmentSetback2d(polygon),
      globalIndex: index,
      pitch: polygon?.details?.pitch,
      area: polygon?.details?.area,
      azimuth: polygon?.details?.azimu,
    }))
    .filter((seg) => seg.points.length >= 3);

  if (!rawSegments.length) {
    toast.warning("Please draw sections to generate Roof Sections Report.", {
      key: "facet-report-no-segments",
      duration: 5000,
    });
    return;
  }

  const doc = new jsPDF({
    unit: "pt",
    format: "letter",
    orientation: "portrait",
    compress: true,
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const left = 28;
  const right = 28;
  const contentW = pageWidth - left - right;
  const boxesPerPage = 24;
  const overviewPages = Math.max(1, Math.ceil(rawSegments.length / boxesPerPage));
  const facetDetailPages = rawSegments.length;
  const flightPages = 1;
  const totalPages = overviewPages + facetDetailPages + flightPages;
  const detailPageStart = overviewPages + 1;

  const sharedCtx = {
    pageWidth,
    pageHeight,
    left,
    right,
    customerName: getCustomerName(currentDesign),
    customerAddress: getAddress(currentDesign),
    designerName: getDesignerName(currentDesign),
    logoDataUrl: organizationLogoDataUrl,
    logoPlacement: fitImageContain(logoDimensions, left + 1, 25, 168, 30, {
      alignX: "left",
    }),
  };
  const normalized = await normalizeFacetReportData(
    currentDesign,
    reportData,
    contentW / FLIGHT_INFO_IMAGE_HEIGHT
  );

  let page = 1;
  for (let p = 0; p < overviewPages; p++) {
    if (page > 1) doc.addPage();
    const start = p * boxesPerPage;
    const end = start + boxesPerPage;
    drawOverviewPage({
      doc,
      pageNumber: page,
      overviewPageNumber: p + 1,
      overviewPageTotal: overviewPages,
      allSegments: rawSegments,
      segmentsForBoxes: rawSegments.slice(start, end),
      detailPageStart,
      sharedCtx,
    });
    page += 1;
  }

  for (const [idx, segment] of rawSegments.entries()) {
    doc.addPage();
    await drawFacetDetailPage({
      doc,
      pageNumber: page,
      segment,
      allSegments: rawSegments,
      segmentIdx: idx,
      sharedCtx,
    });
    page += 1;
  }

  doc.addPage();
  await drawFlightInfoPage({
    doc,
    pageNumber: page,
    sharedCtx,
    mapTileImage: normalized.mapTileImage,
    flight: normalized.flight,
    altitudes: normalized.altitudes,
  });

  const base =
    pickFirstNonEmpty(currentDesign?.name, currentDesign?.id, currentDesign?.uuid) || "design";
  const fileName = `${base}-facet-report.pdf`;
  const pdfArrayBuffer = doc.output("arraybuffer");
  const pdfBlob = new Blob([pdfArrayBuffer], { type: "application/pdf" });

  await uploadDesignerReport({
    token: reportData?.auth?.token || "",
    projectId: reportData?.project?.id || "",
    reportType: "facet",
    fileBlob: pdfBlob,
    fileName,
  });

  // shouldDownload defaults to true so the interactive Designer keeps saving a
  // local copy. Headless routes pass shouldDownload:false to suppress the
  // browser save (the file is delivered via upload + redirect only).
  if (reportData?.shouldDownload !== false) {
    doc.save(fileName);
  }
}
